import { useSyncExternalStore } from 'react';
import { subscribeConnection, connectionStatus, type ConnectionStatus } from './useOnlineStatus';

export type { ConnectionStatus };
export { RECONNECT_PROBE_MS } from './useOnlineStatus';

/**
 * Three states, where the app used to have two.
 *
 * `useOnlineStatus` answers a yes/no question and several places still want
 * exactly that. This one answers the question a user actually has:
 *
 * - **`online`** — the browser has a network *and* the backend is answering.
 * - **`offline`** — the browser says there is no network. Nothing will reach
 *   the server, and cached mail is all there is.
 * - **`reconnecting`** — the interface is up but we have not heard from the
 *   backend since it came back, or a request has just failed to reach it. The
 *   important difference from `offline` is that this one resolves itself:
 *   there is nothing for the user to do but wait a moment.
 *
 * Only the *source* lives here. Rendering the shell's indicator belongs to
 * `components/OfflineIndicator.tsx`, which is another change's to make.
 */
export function useConnectionStatus(): ConnectionStatus {
  // `useSyncExternalStore`, not state-plus-effect: the snapshot is a plain
  // string, so it is stable by value, and the store is a genuinely external
  // one (two `window` events and a module flag the transport writes).
  return useSyncExternalStore(subscribeConnection, connectionStatus, connectionStatus);
}
